import { animated } from '@react-spring/web'
import React, { PropsWithChildren, useState } from 'react'
import { Button, ButtonProps } from '~/components/Button'
import { useTranslations } from '~/components/Locale'
import { Promo } from '~/components/utils'
import type { HTMLElementProps, ProductListing } from '~/types'
import { cn, imageUrl, productUrl } from '~/utils'

const Action: React.FC<ButtonProps> = ({ className, children, ...props }) => (
	<Button
		{...props}
		className={cn(
			'bg-green-600 font-bold sm:px-5 sm:py-3 sm:text-xl',
			{ 'px-5 py-3 text-xl': props.size !== 'sm' },
			className
		)}
	>
		{children}
	</Button>
)

const Actions: React.FC<HTMLElementProps<'div'>> = ({ className, children, ...props }) => (
	<div {...props} className={cn('flex space-x-2', className)}>
		{children}
	</div>
)

export interface ContainerProps extends PropsWithChildren {
	size?: 'small' | 'medium' | 'large'
	className?: string
	product: ProductListing
	bg?: string
}

const Container: React.FC<ContainerProps> = ({ className, size = 'large', product, bg, children }) => {
	const t = useTranslations('react')
	const [hover, setHover] = useState(false)
	const opaque = product.opaque?.value === 'true'
	return (
		<animated.li
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			className={cn(
				'safari-overflow-fix relative h-96 overflow-hidden rounded-3xl bg-white text-3xl',
				{
					'h-52 sm:h-96': size == 'small',
					'h-72 sm:h-96': size == 'medium',
					'h-96': size == 'large'
				},
				className
			)}
		>
			<img
				className={cn(
					'absolute z-0 h-full w-full transform-gpu transition-transform duration-300 ease-out',
					opaque ? 'object-cover' : 'object-contain',
					hover ? 'scale-100' : 'scale-110'
				)}
				src={imageUrl(product.featuredImage, 700)}
				loading="lazy"
			/>
			<a
				className={cn('absolute z-[5] h-full w-full', { 'bg-black opacity-30': opaque }, bg)}
				href={productUrl(product)}
			/>
			<div
				className={cn(
					'pointer-events-none absolute z-10 flex h-full w-full flex-col items-start justify-between',
					{
						'p-10': size == 'large' || size == 'medium',
						'p-4 sm:p-10': size == 'small'
					}
				)}
			>
				<div className="pointer-events-auto">
					<h4
						className={cn(
							'mb-3 leading-none',
							{
								'xs:text-3xl text-2xl': size === 'small',
								'text-4xl': size === 'medium' || size === 'large'
							},
							opaque ? 'text-white' : 'text-gray-900'
						)}
					>
						<a href={productUrl(product)}>{product.title}</a>
					</h4>
					<Promo
						promo={product}
						className={
							'inline-block justify-self-start rounded-full px-4 py-2 ' +
							'text-green-550 bg-white text-xl font-bold'
						}
					/>
				</div>
				<div className="pointer-events-auto">
					{product.availableForSale ? (
						children
					) : (
						<Actions>
							<Action href={productUrl(product)} className="bg-stone-300 px-12">
								{t?.auk_page.out_of_stock}
							</Action>
						</Actions>
					)}
				</div>
			</div>
		</animated.li>
	)
}

export const ProductCard = {
	Actions,
	Action,
	Container
}
